import React from "react"
import {
  Box,
  Button,
  Typography,
  Grid,
  IconButton,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import CustomInput from '../Returns/CustomInput'
import CustomSelect from '../Returns/CustomSelect'
import StyledSwitch from '../common/StyledSwitch'

const UserSetupForm = ({
  formData,
  formErrors,
  editingUser,
  formLoading,
  onInputChange,
  onSubmit,
  onClose,
}) => {
  const handleBusinessRoleChange = event => {
    const value = event.target.value
    onInputChange('businessRole', value)
  }

  const businessRoleValue = React.useMemo(() => {
    if (!formData.businessRole) return []
    if (Array.isArray(formData.businessRole)) return formData.businessRole
    if (typeof formData.businessRole === 'string')
      return formData.businessRole ? [formData.businessRole] : []
    return []
  }, [formData.businessRole])

  // inside UserSetupForm component
  const handleEmailChange = e => {
    const email = e.target.value
    onInputChange('email', email)

    // Auto enable SSO if email contains @pwc.com
    if (email.toLowerCase().includes('@pwc.com')) {
      onInputChange('ssoUser', true)
    } else {
      onInputChange('ssoUser', false)
    }
  }


  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        maxWidth: '620px',
        width: '100%',
        mx: 'auto',
        position: 'relative',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Header with title and close button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#333333',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          User Setup
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#666666',
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      </Box>

      {/* Mandatory fields note */}
      <Box sx={{ p: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'var(--forbrand-error)',
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          (*) Fields are mandatory
        </Typography>
        {/* General error message */}
        {formErrors.general && (
          <Typography variant="body2" color="error" sx={{ my: 2 }}>
            {formErrors.general}
          </Typography>
        )}
      </Box>

      {/* Form container */}
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{ px: 3, pb: 4, display: 'flex', gap: 2, flexDirection: 'column' }}
      >
        {/* User Setup Form - Grid Layout */}
        <Grid container spacing={3}>
          {/* Row 1 */}
          <Grid item size={6}>
            <CustomInput
              label="User ID"
              required
              placeholder="Enter User ID"
              value={formData.userId || ''}
              onChange={e => onInputChange('userId', e.target.value)}
              error={!!formErrors.userId}
              helperText={formErrors.userId}
              disabled={!!editingUser}
              maxLength={50}
            />
          </Grid>
          <Grid item size={6}>
            <CustomInput
              label="User Name"
              required
              placeholder="Enter User Name"
              value={formData.userName || ''}
              onChange={e => onInputChange('userName', e.target.value)}
              error={!!formErrors.userName}
              helperText={formErrors.userName}
              maxLength={200}
            />
          </Grid>

          {/* Row 2 */}
          <Grid item size={6}>
            <CustomInput
              label="Email ID"
              required
              placeholder="Enter Email ID"
              type="email"
              value={formData.email || ''}
              onChange={handleEmailChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              maxLength={200}
            />
          </Grid>
          <Grid item size={6}>
            <CustomSelect
              label="Business Role"
              fullWidth
              required
              placeholder="Select Business Role"
              value={businessRoleValue[0] || ''}
              onChange={handleBusinessRoleChange}
              options={[
                { label: 'Preparer', value: 'PREPARER' },
                { label: 'Admin', value: 'ADMIN' },
              ]}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.value}
              disabled={!!editingUser}
              error={!!formErrors.businessRole}
              helperText={formErrors.businessRole}
            />
          </Grid>

          {/* Row 3 */}
          <Grid item size={6}>
            <CustomSelect
              label="Is Locked"
              fullWidth
              required
              placeholder="Select Option"
              value={
                formData.isLocked !== undefined
                  ? String(formData.isLocked)
                  : ''
              }
              onChange={e =>
                onInputChange('isLocked', e.target.value === 'true')
              }
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.value}
            />
          </Grid>
          <Grid item size={6}>
            <CustomSelect
              label="Active"
              required
              fullWidth
              placeholder="Select Option"
              value={
                formData.isActive !== undefined
                  ? String(formData.isActive)
                  : ''
              }
              onChange={e =>
                onInputChange('isActive', e.target.value === 'true')
              }
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.value}
            />
          </Grid>

          {/* SSO toggle */}
          <Grid
            item
            size={6}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: '16px',
                color: '#333333',
                fontWeight: 500,
              }}
            >
              SSO
            </Typography>
            <StyledSwitch
              checked={!!formData.ssoUser}
              onChange={e => onInputChange('ssoUser', e.target.checked)}
            />
          </Grid>
        </Grid>

        {/* Save button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingTop: '40px',
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formLoading}
            sx={{
              backgroundColor: 'var( --forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              px: '24px',
              py: '8px',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: '4px',
              '&:disabled': {
                backgroundColor: '#ccc',
                boxShadow: 'none',
              },
            }}
          >
            {formLoading ? 'Saving...' : editingUser ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default UserSetupForm
